import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  FileText, Send, CheckCircle, Plus, ChevronDown, ChevronRight,
  Trash2, Edit, Copy, ArrowRight, Euro, Clock, Search,
  Building2, X, Eye, Pen, Download, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  devis: 'Devis',
  bon_livraison: 'Bon de livraison',
  reception_chantier: 'Réception chantier',
  facture: 'Facture'
};

const TYPE_COLORS = {
  devis: 'bg-blue-50 text-blue-700 border-blue-200',
  bon_livraison: 'bg-amber-50 text-amber-700 border-amber-200',
  reception_chantier: 'bg-purple-50 text-purple-700 border-purple-200',
  facture: 'bg-emerald-50 text-emerald-700 border-emerald-200'
};

const STATUT_COLORS = {
  brouillon: 'bg-stone-200 text-stone-800',
  valide: 'bg-blue-100 text-blue-700',
  envoye: 'bg-indigo-100 text-indigo-700',
  signe: 'bg-purple-100 text-purple-700',
  accepte: 'bg-green-100 text-green-700',
  paye: 'bg-emerald-100 text-emerald-800',
  refuse: 'bg-red-100 text-red-700',
  annule: 'bg-stone-100 text-stone-500'
};

const UNITES = ['h', 'ha', 'km', 'u', 't', 'kg', 'l', 'jour', 'forfait'];

const ECHEANCES = [
  { value: 'reception', label: 'À réception' },
  { value: '15', label: '15 jours' },
  { value: '30', label: '30 jours' },
  { value: '45', label: '45 jours' },
  { value: '60', label: '60 jours' },
  { value: 'fin_mois', label: 'Fin de mois' },
  { value: 'custom', label: 'Date personnalisée' },
];

function calculerDateEcheance(dateFacture, echeance) {
  if (!dateFacture || !echeance || echeance === 'custom') return '';
  const d = new Date(dateFacture);
  if (echeance === 'reception') return dateFacture;
  if (echeance === 'fin_mois') {
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return end.toISOString().slice(0, 10);
  }
  const jours = parseInt(echeance, 10);
  if (!isNaN(jours)) {
    d.setDate(d.getDate() + jours);
    return d.toISOString().slice(0, 10);
  }
  return '';
}

const num = (v) => parseFloat(String(v ?? '').replace(',', '.')) || 0;
const saisieDecimale = (v) => v.replace(/[^0-9,.-]/g, '');

function formatMoney(val) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val || 0);
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ==================== COMPOSANT FORMULAIRE ====================
function DocumentForm({ type, onSave, onCancel, initial, clientsRecents }) {
  const [client, setClient] = useState(initial?.client || { nom: '', email: '', siret: '', siren: '', tva_intra: '', adresse: '', complement_adresse: '', code_postal: '', ville: '', telephone: '' });
  const [lignes, setLignes] = useState(initial?.lignes || [{ reference: '', description: '', quantite: 1, unite: 'h', prix_unitaire_ht: 0, taux_tva: 20 }]);
  const [objet, setObjet] = useState(initial?.objet || '');
  const [conditions, setConditions] = useState(initial?.conditions_paiement || 'Paiement à 30 jours');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [refChantier, setRefChantier] = useState(initial?.reference_chantier || '');
  const [dateFacture, setDateFacture] = useState(initial?.date_facture || new Date().toISOString().slice(0, 10));
  const [echeanceType, setEcheanceType] = useState(initial?.echeance_type || '30');
  const [dateEcheance, setDateEcheance] = useState(initial?.date_echeance || '');
  const [showClients, setShowClients] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sireneResults, setSireneResults] = useState([]);
  const [sireneLoading, setSireneLoading] = useState(false);
  const [sireneOpen, setSireneOpen] = useState(false);

  const addLigne = () => setLignes([...lignes, { reference: '', description: '', quantite: 1, unite: 'h', prix_unitaire_ht: 0, taux_tva: 20 }]);
  const removeLigne = (i) => setLignes(lignes.filter((_, idx) => idx !== i));
  const updateLigne = (i, field, val) => {
    const updated = [...lignes];
    updated[i] = { ...updated[i], [field]: val };
    setLignes(updated);
  };

  // Auto-remplissage SIRET : appelle l'API gouv
  const rechercherSirene = async () => {
    const query = (client.siret || client.nom || '').trim();
    if (query.length < 2) {
      toast.error('Tapez un nom ou un SIRET avant de rechercher');
      return;
    }
    setSireneLoading(true);
    setSireneOpen(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/sirene/search?q=${encodeURIComponent(query)}&limit=8`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      setSireneResults(data.results || []);
      if ((data.results || []).length === 0) {
        toast.info('Aucune entreprise trouvée sur recherche-entreprises.api.gouv.fr');
      }
    } catch (e) {
      toast.error('Erreur recherche SIRET');
    } finally {
      setSireneLoading(false);
    }
  };

  const appliquerSirene = (e) => {
    setClient({
      ...client,
      nom: e.raison_sociale || e.nom_complet || client.nom,
      siret: e.siret || client.siret,
      siren: e.siren || '',
      tva_intra: e.tva_intra || '',
      adresse: e.adresse || client.adresse,
      code_postal: e.code_postal || client.code_postal,
      ville: e.ville || client.ville,
    });
    setSireneOpen(false);
    setSireneResults([]);
    toast.success('Informations SIRET appliquées');
  };

  // Recalcul échéance quand date facture ou type change
  useEffect(() => {
    if (echeanceType !== 'custom') {
      const d = calculerDateEcheance(dateFacture, echeanceType);
      if (d) setDateEcheance(d);
    }
  }, [dateFacture, echeanceType]);

  const totalHT = lignes.reduce((s, l) => s + (num(l.quantite) * num(l.prix_unitaire_ht) * (1 - (l.remise_percent || 0) / 100)), 0);
  const totalTVA = lignes.reduce((s, l) => {
    const ht = num(l.quantite) * num(l.prix_unitaire_ht) * (1 - (l.remise_percent || 0) / 100);
    return s + ht * (l.taux_tva || 20) / 100;
  }, 0);

  const handleSave = async () => {
    if (!client.nom) { toast.error('Nom du client requis'); return; }
    if (lignes.length === 0 || !lignes[0].description) { toast.error('Au moins une ligne requise'); return; }
    setSaving(true);
    try {
      await onSave({
        type_document: type,
        client,
        lignes: lignes.map(l => ({ ...l, quantite: num(l.quantite), prix_unitaire_ht: num(l.prix_unitaire_ht) })),
        objet,
        conditions_paiement: conditions,
        notes,
        reference_chantier: refChantier,
        date_facture: dateFacture,
        date_echeance: dateEcheance,
        echeance_type: echeanceType,
      });
    } finally {
      setSaving(false);
    }
  };

  const selectClient = (c) => {
    setClient({ ...client, ...c });
    setShowClients(false);
  };

  return (
    <div className="space-y-5" data-testid="document-form">
      {/* Client */}
      <div className="bg-white border border-stone-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2"><Building2 size={16} /> Client</h3>
          {clientsRecents?.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowClients(!showClients)} className="text-xs text-blue-700">
              Clients récents {showClients ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </Button>
          )}
        </div>
        {showClients && (
          <div className="mb-3 flex flex-wrap gap-2">
            {clientsRecents.map((c, i) => (
              <button key={i} onClick={() => selectClient(c)}
                className="px-3 py-1.5 text-xs bg-stone-100 border border-stone-300 rounded-full hover:border-blue-500 transition-colors text-stone-800">
                {c.nom}
              </button>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input placeholder="Nom / Raison sociale *" value={client.nom} onChange={e => setClient({ ...client, nom: e.target.value })}
            className="bg-white border-stone-300" data-testid="client-nom" />
          <Input placeholder="Email" value={client.email} onChange={e => setClient({ ...client, email: e.target.value })}
            className="bg-white border-stone-300" data-testid="client-email" />
          <div className="flex gap-2">
            <Input placeholder="SIRET (14 chiffres)" value={client.siret} onChange={e => setClient({ ...client, siret: e.target.value })}
              className="bg-white border-stone-300 flex-1" data-testid="client-siret" maxLength={14} />
            <Button size="sm" variant="outline" onClick={rechercherSirene} disabled={sireneLoading} className="shrink-0" data-testid="btn-sirene-search">
              {sireneLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search size={14} />}
              <span className="ml-1 hidden sm:inline">Rechercher</span>
            </Button>
          </div>
          <Input placeholder="Téléphone" value={client.telephone} onChange={e => setClient({ ...client, telephone: e.target.value })}
            className="bg-white border-stone-300" />
          <Input placeholder="Adresse" value={client.adresse} onChange={e => setClient({ ...client, adresse: e.target.value })}
            className="bg-white border-stone-300" data-testid="client-adresse" />
          <Input placeholder="Complément d'adresse (bâtiment, étage, lieu-dit...)" value={client.complement_adresse || ''} onChange={e => setClient({ ...client, complement_adresse: e.target.value })}
            className="bg-white border-stone-300" data-testid="client-complement" />
          <div className="flex gap-2">
            <Input placeholder="CP" value={client.code_postal} onChange={e => setClient({ ...client, code_postal: e.target.value })}
              className="bg-white border-stone-300 w-28" />
            <Input placeholder="Ville" value={client.ville} onChange={e => setClient({ ...client, ville: e.target.value })}
              className="bg-white border-stone-300 flex-1" />
          </div>
          <Input placeholder="N° TVA Intracommunautaire (auto si SIRET)" value={client.tva_intra || ''} onChange={e => setClient({ ...client, tva_intra: e.target.value })}
            className="bg-white border-stone-300" data-testid="client-tva-intra" />
        </div>
        {sireneOpen && sireneResults.length > 0 && (
          <div className="mt-3 max-h-60 overflow-y-auto border border-stone-300 rounded-lg bg-white" data-testid="sirene-results">
            {sireneResults.map((r, i) => (
              <button key={i} onClick={() => appliquerSirene(r)}
                className="w-full text-left px-3 py-2 hover:bg-stone-100 border-b border-stone-200 text-sm">
                <div className="font-medium text-stone-900">{r.raison_sociale || r.nom_complet}</div>
                <div className="text-xs text-stone-600">SIRET {r.siret} — {r.adresse}, {r.code_postal} {r.ville}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Détails + Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input placeholder="Objet du document" value={objet} onChange={e => setObjet(e.target.value)}
          className="bg-white border-stone-200" data-testid="doc-objet" />
        <Input placeholder="Référence chantier" value={refChantier} onChange={e => setRefChantier(e.target.value)}
          className="bg-white border-stone-200" />
        <div>
          <label className="text-xs text-stone-600 block mb-1">Date {type === 'facture' ? 'de facture' : 'du document'} *</label>
          <Input type="date" value={dateFacture} onChange={e => setDateFacture(e.target.value)}
            className="bg-white border-stone-200" data-testid="date-facture" />
        </div>
        <div>
          <label className="text-xs text-stone-600 block mb-1">Échéance</label>
          <div className="flex gap-2">
            <select value={echeanceType} onChange={e => setEcheanceType(e.target.value)}
              className="bg-white border border-stone-300 rounded-md text-sm text-stone-800 px-2 py-1.5 flex-1"
              data-testid="echeance-type">
              {ECHEANCES.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
            {echeanceType === 'custom' && (
              <Input type="date" value={dateEcheance} onChange={e => setDateEcheance(e.target.value)}
                className="bg-white border-stone-200 flex-1" data-testid="date-echeance-custom" />
            )}
            {echeanceType !== 'custom' && dateEcheance && (
              <span className="text-xs text-stone-500 self-center whitespace-nowrap">→ {formatDate(dateEcheance)}</span>
            )}
          </div>
        </div>
      </div>

      {/* Lignes */}
      <div className="bg-white border border-stone-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-stone-800 mb-3">Lignes de prestation</h3>
        <div className="space-y-2">
          {lignes.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <Input placeholder="Réf." value={l.reference || ''} onChange={e => updateLigne(i, 'reference', e.target.value)}
                className="col-span-2 bg-white border-stone-300 text-sm" data-testid={`ligne-ref-${i}`} />
              <Input placeholder="Description *" value={l.description} onChange={e => updateLigne(i, 'description', e.target.value)}
                className="col-span-3 bg-white border-stone-300 text-sm" data-testid={`ligne-desc-${i}`} />
              <Input type="text" inputMode="decimal" placeholder="Qté" value={l.quantite} onChange={e => updateLigne(i, 'quantite', saisieDecimale(e.target.value))}
                data-testid={`ligne-qte-${i}`} className="col-span-1 bg-white border-stone-300 text-sm text-right" />
              <select value={l.unite} onChange={e => updateLigne(i, 'unite', e.target.value)}
                className="col-span-1 bg-white border border-stone-300 rounded-md text-sm text-stone-800 px-2 py-1.5">
                {UNITES.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
              <Input type="text" inputMode="decimal" placeholder="PU HT" value={l.prix_unitaire_ht} onChange={e => updateLigne(i, 'prix_unitaire_ht', saisieDecimale(e.target.value))}
                className="col-span-2 bg-white border-stone-300 text-sm text-right" data-testid={`ligne-pu-${i}`} />
              <select value={l.taux_tva} onChange={e => updateLigne(i, 'taux_tva', parseFloat(e.target.value))}
                className="col-span-1 bg-white border border-stone-300 rounded-md text-sm text-stone-800 px-2 py-1.5"
                data-testid={`ligne-tva-${i}`}>
                <option value={20}>20%</option>
                <option value={10}>10%</option>
                <option value={5.5}>5.5%</option>
                <option value={2.1}>2.1%</option>
                <option value={0}>0%</option>
              </select>
              <div className="col-span-2 text-right text-sm text-stone-600 flex items-center justify-between">
                <span>{formatMoney(num(l.quantite) * num(l.prix_unitaire_ht))}</span>
                <button onClick={() => removeLigne(i)} className="text-red-400 hover:text-red-600 ml-2">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={addLigne} className="mt-3 text-blue-700" data-testid="add-ligne-btn">
          <Plus size={14} className="mr-1" /> Ajouter une ligne
        </Button>

        {/* Totaux */}
        <div className="mt-4 pt-4 border-t border-stone-200 space-y-1 text-right">
          <div className="text-sm text-stone-600">Total HT : <span className="text-stone-900 font-medium">{formatMoney(totalHT)}</span></div>
          <div className="text-sm text-stone-600">Total TVA : <span className="text-stone-900 font-medium">{formatMoney(totalTVA)}</span></div>
          <div className="text-base text-stone-900 font-bold">Total TTC : {formatMoney(totalHT + totalTVA)}</div>
        </div>
      </div>

      {/* Notes et conditions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <textarea placeholder="Conditions de paiement" value={conditions} onChange={e => setConditions(e.target.value)}
          className="bg-white border border-stone-200 rounded-lg p-3 text-sm text-stone-800 resize-none h-20" />
        <textarea placeholder="Notes / mentions" value={notes} onChange={e => setNotes(e.target.value)}
          className="bg-white border border-stone-200 rounded-lg p-3 text-sm text-stone-800 resize-none h-20" />
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <Button variant="ghost" onClick={onCancel} className="text-stone-600">Annuler</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-doc-btn">
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
}

// ==================== COMPOSANT DÉTAIL DOCUMENT ====================
function ChangerSociete({ doc, societes, onDeplacer, disabled }) {
  const autres = (societes || []).filter(s => s.id !== doc.societe_id);
  if (autres.length === 0) return null;
  return (
    <select defaultValue="" disabled={disabled} onChange={e => { if (e.target.value) onDeplacer(e.target.value); e.target.value = ''; }}
      className="bg-white border border-stone-300 rounded-md text-sm text-stone-800 px-2 py-1.5" data-testid="changer-societe-select">
      <option value="" disabled>Changer de société…</option>
      {autres.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
    </select>
  );
}

function DocumentDetail({ doc, onBack, onAction, actionLoading, societes, onSupprimer, onDeplacer }) {
  const chainLabels = { devis: 'BL', bon_livraison: 'Réception', reception_chantier: 'Facture' };
  const nextLabel = chainLabels[doc.type_document];

  return (
    <div className="space-y-5" data-testid="document-detail">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-blue-700 text-sm hover:underline">&larr; Retour</button>
        <div className="flex flex-wrap gap-2 justify-end">
          {doc.statut === 'brouillon' && (
            <Button size="sm" onClick={() => onAction('valider')} disabled={actionLoading} className="bg-blue-600 hover:bg-blue-700" data-testid="valider-btn">
              <CheckCircle size={14} className="mr-1" /> Valider
            </Button>
          )}
          {['valide', 'envoye'].includes(doc.statut) && doc.client?.email && (
            <Button size="sm" onClick={() => onAction('envoyer')} disabled={actionLoading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="envoyer-btn">
              <Send size={14} className="mr-1" /> Envoyer par email
            </Button>
          )}
          {nextLabel && doc.statut !== 'brouillon' && !(doc.type_document === 'reception_chantier' && !doc.signature) && (
            <Button size="sm" onClick={() => onAction('convertir')} disabled={actionLoading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="convertir-btn">
              <ArrowRight size={14} className="mr-1" /> Créer {nextLabel}
            </Button>
          )}
          {doc.type_document === 'facture' && doc.statut !== 'paye' && doc.statut !== 'brouillon' && (
            <Button size="sm" onClick={() => onAction('payer')} disabled={actionLoading} className="bg-green-600 hover:bg-green-700" data-testid="payer-btn">
              <Euro size={14} className="mr-1" /> Marquer payé
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onAction('dupliquer')} disabled={actionLoading} data-testid="dupliquer-btn">
            <Copy size={14} className="mr-1" /> Dupliquer
          </Button>
          <Button size="sm" onClick={() => onAction('pdf')} disabled={actionLoading} className="bg-stone-700 text-white hover:bg-stone-800" data-testid="pdf-btn">
            <Download size={14} className="mr-1" /> PDF
          </Button>
          {!['brouillon'].includes(doc.statut) && doc.statut !== 'signe' && (
            <Button size="sm" onClick={() => onAction('demander_signature')} disabled={actionLoading}
                    className="bg-emerald-600 hover:bg-emerald-700" data-testid="demander-signature-btn">
              ✍️ Envoyer signature
            </Button>
          )}
          <ChangerSociete doc={doc} societes={societes} onDeplacer={onDeplacer} disabled={actionLoading} />
          {doc.statut !== 'paye' && (
            <Button size="sm" variant="outline" onClick={onSupprimer} disabled={actionLoading}
              className="border-red-300 text-red-700 hover:bg-red-50" data-testid="supprimer-doc-btn">
              <Trash2 size={14} className="mr-1" /> Supprimer
            </Button>
          )}
        </div>
      </div>

      {/* En-tête */}
      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${TYPE_COLORS[doc.type_document]}`}>
                {TYPE_LABELS[doc.type_document]}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs ${STATUT_COLORS[doc.statut] || 'bg-stone-200 text-stone-800'}`}>
                {doc.statut}
              </span>
              {doc.inter_societes && <span className="px-2 py-0.5 rounded text-xs bg-orange-100 text-orange-700">Inter-sociétés</span>}
            </div>
            <h2 className="text-2xl font-bold text-stone-900 mt-2">{doc.numero}</h2>
            <p className="text-sm text-stone-500 mt-1">{doc.objet}</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-stone-500">Créé le {formatDate(doc.date_creation)}</div>
            {doc.date_envoi && <div className="text-sm text-indigo-700">Envoyé le {formatDate(doc.date_envoi)}</div>}
            {doc.date_paiement && <div className="text-sm text-emerald-700">Payé le {formatDate(doc.date_paiement)}</div>}
          </div>
        </div>

        {/* Client */}
        <div className="mt-5 p-4 bg-stone-50 rounded-lg">
          <div className="text-xs text-stone-500 uppercase tracking-wide mb-1">Client</div>
          <div className="text-stone-900 font-medium">{doc.client?.nom}</div>
          {doc.client?.siret && <div className="text-xs text-stone-500">SIRET: {doc.client.siret}</div>}
          {doc.client?.adresse && <div className="text-xs text-stone-500">{doc.client.adresse} {doc.client.code_postal} {doc.client.ville}</div>}
          {doc.client?.email && <div className="text-xs text-blue-700">{doc.client.email}</div>}
        </div>

        {/* Lignes */}
        <table className="w-full mt-5">
          <thead>
            <tr className="text-xs text-stone-500 uppercase tracking-wide border-b border-stone-200">
              <th className="text-left pb-2 pl-2">Description</th>
              <th className="text-right pb-2">Qté</th>
              <th className="text-right pb-2">Unité</th>
              <th className="text-right pb-2">PU HT</th>
              <th className="text-right pb-2">TVA</th>
              <th className="text-right pb-2 pr-2">Total HT</th>
            </tr>
          </thead>
          <tbody>
            {doc.lignes?.map((l, i) => (
              <tr key={i} className="border-b border-stone-100 text-sm">
                <td className="py-2.5 pl-2 text-stone-800">{l.description}</td>
                <td className="py-2.5 text-right text-stone-600">{Number(l.quantite).toLocaleString('fr-FR')}</td>
                <td className="py-2.5 text-right text-stone-500">{l.unite}</td>
                <td className="py-2.5 text-right text-stone-600">{formatMoney(l.prix_unitaire_ht)}</td>
                <td className="py-2.5 text-right text-stone-500">{l.taux_tva}%</td>
                <td className="py-2.5 text-right pr-2 text-stone-900 font-medium">{formatMoney(l.montant_ht)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 pt-3 border-t border-stone-200 space-y-1 text-right">
          <div className="text-sm text-stone-500">Total HT : <span className="text-stone-800">{formatMoney(doc.total_ht)}</span></div>
          <div className="text-sm text-stone-500">Total TVA : <span className="text-stone-800">{formatMoney(doc.total_tva)}</span></div>
          <div className="text-lg font-bold text-stone-900">Total TTC : {formatMoney(doc.total_ttc)}</div>
        </div>

        {/* Signature */}
        {doc.type_document === 'reception_chantier' && (
          <div className="mt-5 p-4 rounded-lg border border-dashed border-stone-300">
            {doc.signature ? (
              <div className="text-center">
                <CheckCircle className="mx-auto text-emerald-700 mb-2" size={24} />
                <div className="text-emerald-700 font-medium">Signé par {doc.signature.signataire_nom}</div>
                <div className="text-xs text-stone-500">{doc.signature.signataire_fonction} - {formatDate(doc.signature.date_signature)}</div>
              </div>
            ) : (
              <div className="text-center text-stone-500">
                <Pen className="mx-auto mb-2" size={24} />
                <div className="text-sm">En attente de signature</div>
                <div className="text-xs mt-1">Envoyez ce document au client pour signature</div>
              </div>
            )}
          </div>
        )}

        {/* Chaîne documentaire */}
        {doc.chaine_documents && doc.chaine_documents.length > 1 && (
          <div className="mt-5 flex items-center gap-2 text-xs text-stone-500">
            <span>Chaîne :</span>
            {doc.chaine_documents.map((n, i) => (
              <React.Fragment key={i}>
                {i > 0 && <ArrowRight size={12} />}
                <span className={n === doc.numero ? 'text-blue-700 font-medium' : ''}>{n}</span>
              </React.Fragment>
            ))}
          </div>
        )}

        {doc.converti_en_numero && (
          <div className="mt-2 text-xs text-emerald-700">
            Converti en : {doc.converti_en_numero}
          </div>
        )}
      </div>

      {/* Historique */}
      <div className="bg-white border border-stone-200 rounded-lg p-4">
        <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Historique</h3>
        <div className="space-y-2">
          {doc.historique?.map((h, i) => (
            <div key={i} className="flex items-center gap-3 text-xs">
              <Clock size={12} className="text-stone-400" />
              <span className="text-stone-500">{formatDate(h.date)}</span>
              <span className="text-stone-600">{h.action}</span>
              {h.email && <span className="text-blue-700">{h.email}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==================== COMPOSANT STATS ====================
function StatsPanel({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="stats-panel">
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-center">
        <div className="text-2xl font-bold text-blue-700">{stats.par_type?.devis?.count || 0}</div>
        <div className="text-xs text-stone-500">Devis</div>
        <div className="text-xs text-blue-700 mt-1">{formatMoney(stats.par_type?.devis?.total_ht)}</div>
      </div>
      <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-center">
        <div className="text-2xl font-bold text-amber-700">{stats.par_type?.bon_livraison?.count || 0}</div>
        <div className="text-xs text-stone-500">Bons de livraison</div>
      </div>
      <div className="bg-purple-50 border border-purple-100 rounded-lg p-3 text-center">
        <div className="text-2xl font-bold text-purple-700">{stats.par_type?.reception_chantier?.count || 0}</div>
        <div className="text-xs text-stone-500">Réceptions</div>
      </div>
      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-center">
        <div className="text-2xl font-bold text-emerald-700">{stats.par_type?.facture?.count || 0}</div>
        <div className="text-xs text-stone-500">Factures</div>
        <div className="text-xs text-emerald-700 mt-1">{formatMoney(stats.par_type?.facture?.total_ht)}</div>
      </div>
      <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-center">
        <div className="text-2xl font-bold text-red-700">{stats.nb_impayees || 0}</div>
        <div className="text-xs text-stone-500">Impayées</div>
        <div className="text-xs text-red-700 mt-1">{formatMoney(stats.total_impaye_ttc)}</div>
      </div>
    </div>
  );
}

// ==================== PAGE PRINCIPALE ====================
export default function GestionCommercialePage() {
  const { token } = useAuth();
  const { activeSociete: currentSociete, societes, loading: wsLoading } = useWorkspace();
  const [activeTab, setActiveTab] = useState('devis');
  const [documents, setDocuments] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [clientsRecents, setClientsRecents] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
    if (currentSociete?.id) h['X-Societe-ID'] = currentSociete.id;
    return h;
  }, [token, currentSociete]);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/gestion-commerciale/documents?type_document=${activeTab}&page=${page}&limit=30`;
      if (search) url += `&client_nom=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: headers() });
      const data = await res.json();
      setDocuments(data.documents || []);
      setTotalDocs(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, search, headers]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/gestion-commerciale/stats?periode=annee`, { headers: headers() });
      setStats(await res.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/gestion-commerciale/clients-recents`, { headers: headers() });
      const data = await res.json();
      setClientsRecents(data.clients || []);
    } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => { if (!wsLoading) fetchDocuments(); }, [fetchDocuments, wsLoading]);
  useEffect(() => { if (!wsLoading) { fetchStats(); fetchClients(); } }, [fetchStats, fetchClients, wsLoading]);

  const handleSave = async (docData) => {
    try {
      const url = editDoc
        ? `${API}/api/gestion-commerciale/documents/${editDoc.id}`
        : `${API}/api/gestion-commerciale/documents`;
      const method = editDoc ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(docData) });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || 'Erreur'); return; }
      toast.success(editDoc ? 'Document modifié' : `${TYPE_LABELS[activeTab]} créé`);
      setShowForm(false);
      setEditDoc(null);
      fetchDocuments();
      fetchStats();
    } catch (e) {
      toast.error('Erreur réseau');
    }
  };

  const handleAction = async (action) => {
    if (!selectedDoc) return;

    // PDF = téléchargement direct, pas d'API JSON
    if (action === 'pdf') {
      try {
        const res = await fetch(`${API}/api/gestion-commerciale/documents/${selectedDoc.id}/pdf`, { headers: headers() });
        if (!res.ok) { toast.error('Erreur génération PDF'); return; }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedDoc.numero}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('PDF téléchargé');
      } catch { toast.error('Erreur réseau'); }
      return;
    }

    // Demande signature électronique
    if (action === 'demander_signature') {
      const email = window.prompt(
        'Email du destinataire pour la signature :',
        selectedDoc.client?.email || ''
      );
      if (!email || !email.includes('@')) {
        if (email) toast.error('Email invalide');
        return;
      }
      setActionLoading(true);
      try {
        // Mapping doc type GestionCommerciale → doc_type signatures
        const docTypeMap = {
          devis: 'devis',
          bon_livraison: 'bon_livraison',
          reception_chantier: 'bon_reception_chantier',
          facture: 'facture',
        };
        // Récupère le PDF en base64
        const pdfRes = await fetch(`${API}/api/gestion-commerciale/documents/${selectedDoc.id}/pdf`, { headers: headers() });
        const pdfBlob = await pdfRes.blob();
        const pdfBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(pdfBlob);
        });

        const r = await fetch(`${API}/api/signatures/demander`, {
          method: 'POST',
          headers: { ...headers(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            societe_id: localStorage.getItem('activeSocieteId'),
            doc_type: docTypeMap[selectedDoc.type_document] || 'document_libre',
            titre: `${selectedDoc.numero} - ${selectedDoc.client?.nom || 'Client'}`,
            client_nom: selectedDoc.client?.nom || '',
            destinataire_email: email,
            destinataire_nom: selectedDoc.client?.nom || '',
            expediteur_nom: selectedDoc.societe_nom || '',
            mode_signature: 'email',
            doc_source_id: selectedDoc.id,
            pdf_source_base64: pdfBase64,
            montant_ht: selectedDoc.total_ht,
            creer_entree_compta: ['bon_livraison', 'reception_chantier', 'devis'].includes(selectedDoc.type_document),
          }),
        });
        const data = await r.json();
        if (data.success) {
          toast.success(`Email signature envoyé à ${email} ✉️`);
        } else {
          toast.error(data.detail || 'Échec envoi');
        }
      } catch (e) {
        toast.error('Erreur: ' + e.message);
      } finally { setActionLoading(false); }
      return;
    }

    setActionLoading(true);
    try {
      let url, method = 'POST', body = null;
      switch (action) {
        case 'valider': url = `${API}/api/gestion-commerciale/documents/${selectedDoc.id}/valider`; break;
        case 'envoyer': url = `${API}/api/gestion-commerciale/documents/${selectedDoc.id}/envoyer`; break;
        case 'convertir': url = `${API}/api/gestion-commerciale/documents/${selectedDoc.id}/convertir`; break;
        case 'payer': url = `${API}/api/gestion-commerciale/documents/${selectedDoc.id}/payer?mode_paiement=virement`; break;
        case 'dupliquer': url = `${API}/api/gestion-commerciale/documents/${selectedDoc.id}/dupliquer`; break;
        default: return;
      }
      const res = await fetch(url, { method, headers: headers(), body });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Erreur'); return; }
      toast.success(action === 'convertir' ? `Converti en ${data.nouveau_document?.numero}` : `Action: ${action}`);
      // Refresh
      fetchDocuments();
      fetchStats();
      if (data.nouveau_document) {
        setSelectedDoc(data.nouveau_document);
      } else {
        const refreshed = await fetch(`${API}/api/gestion-commerciale/documents/${selectedDoc.id}`, { headers: headers() });
        setSelectedDoc(await refreshed.json());
      }
    } catch (e) {
      toast.error('Erreur réseau');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSupprimer = async () => {
    if (!window.confirm(`Supprimer définitivement ${selectedDoc.numero} (${selectedDoc.client?.nom || ''}) ?`)) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/gestion-commerciale/documents/${selectedDoc.id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Erreur'); return; }
      toast.success(`${selectedDoc.numero} supprimé`);
      setSelectedDoc(null);
      fetchDocuments();
      fetchStats();
    } catch { toast.error('Erreur réseau'); } finally { setActionLoading(false); }
  };

  const handleDeplacer = async (societeId) => {
    const cible = societes.find(s => s.id === societeId);
    if (!window.confirm(`Ranger ${selectedDoc.numero} dans « ${cible?.nom} » ? Il prendra un nouveau numéro dans cette société.`)) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/gestion-commerciale/documents/${selectedDoc.id}/deplacer`, { method: 'POST', headers: headers(), body: JSON.stringify({ societe_id: societeId }) });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Erreur'); return; }
      toast.success(`Déplacé dans ${cible?.nom} sous le n° ${data.numero}`);
      setSelectedDoc(null);
      fetchDocuments();
      fetchStats();
    } catch { toast.error('Erreur réseau'); } finally { setActionLoading(false); }
  };

  const tabs = [
    { key: 'devis', label: 'Devis', icon: FileText, color: 'text-blue-700' },
    { key: 'bon_livraison', label: 'Bons de livraison', icon: FileText, color: 'text-amber-700' },
    { key: 'reception_chantier', label: 'Réceptions', icon: Pen, color: 'text-purple-700' },
    { key: 'facture', label: 'Factures', icon: Euro, color: 'text-emerald-700' }
  ];

  // Détail doc
  if (selectedDoc) {
    return (
      <div className="p-6 max-w-5xl mx-auto" data-testid="gestion-commerciale-page">
        <DocumentDetail doc={selectedDoc} onBack={() => { setSelectedDoc(null); fetchDocuments(); }}
          onAction={handleAction} actionLoading={actionLoading} societes={societes}
          onSupprimer={handleSupprimer} onDeplacer={handleDeplacer} />
      </div>
    );
  }

  // Formulaire
  if (showForm) {
    return (
      <div className="p-6 max-w-5xl mx-auto" data-testid="gestion-commerciale-page">
        <h2 className="text-lg font-semibold text-stone-900 mb-4">
          {editDoc ? `Modifier ${editDoc.numero}` : `Nouveau ${TYPE_LABELS[activeTab]}`}
        </h2>
        <DocumentForm type={activeTab} onSave={handleSave} onCancel={() => { setShowForm(false); setEditDoc(null); }}
          initial={editDoc} clientsRecents={clientsRecents} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="gestion-commerciale-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Gestion Commerciale</h1>
          <p className="text-sm text-stone-500 mt-0.5">
            {currentSociete?.nom || 'Toutes sociétés'} &middot; Chaîne : Devis &rarr; BL &rarr; Réception &rarr; Facture
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-doc-btn">
          <Plus size={16} className="mr-2" /> Nouveau {TYPE_LABELS[activeTab]}
        </Button>
      </div>

      <StatsPanel stats={stats} />

      {/* Onglets */}
      <div className="flex items-center gap-1 bg-white border border-stone-200 rounded-lg p-1 mb-5">
        {tabs.map(t => (
          <button key={t.key}
            onClick={() => { setActiveTab(t.key); setPage(1); }}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${activeTab === t.key ? 'bg-stone-100 text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-800'}`}
            data-testid={`tab-${t.key}`}>
            <t.icon size={14} className={activeTab === t.key ? t.color : ''} />
            <span className="hidden sm:inline">{t.label}</span>
            {stats?.par_type?.[t.key]?.count > 0 && (
              <span className="text-xs bg-stone-200 px-1.5 rounded">{stats.par_type[t.key].count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Recherche */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
        <Input placeholder="Rechercher par client..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="pl-10 bg-white border-stone-200" data-testid="search-input" />
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center text-stone-500 py-12">Chargement...</div>
      ) : documents.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="mx-auto text-stone-400 mb-3" size={48} />
          <div className="text-stone-500">Aucun {TYPE_LABELS[activeTab].toLowerCase()}</div>
          <Button onClick={() => setShowForm(true)} variant="ghost" className="mt-3 text-blue-700" data-testid="empty-new-btn">
            <Plus size={14} className="mr-1" /> Créer le premier
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map(doc => (
            <div key={doc.id}
              onClick={() => setSelectedDoc(doc)}
              className="flex items-center justify-between bg-white border border-stone-200 rounded-lg px-4 py-3 hover:border-stone-300 cursor-pointer transition-all group"
              data-testid={`doc-row-${doc.id}`}>
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="flex-shrink-0 w-24">
                  <div className="text-sm font-mono text-stone-800">{doc.numero}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-stone-900 truncate">{doc.client?.nom}</div>
                  <div className="text-xs text-stone-500 truncate">{doc.objet || doc.reference_chantier || '-'}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`px-2 py-0.5 rounded text-xs ${STATUT_COLORS[doc.statut] || 'bg-stone-200 text-stone-800'}`}>
                    {doc.statut}
                  </span>
                  {doc.inter_societes && <span className="text-xs text-orange-700">IS</span>}
                  <div className="text-right">
                    <div className="text-sm font-medium text-stone-900">{formatMoney(doc.total_ttc)}</div>
                    <div className="text-xs text-stone-500">{formatDate(doc.date_creation)}</div>
                  </div>
                </div>
              </div>
              <ChevronRight size={16} className="text-stone-400 group-hover:text-stone-700 ml-3 flex-shrink-0" />
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalDocs > 30 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Précédent</Button>
          <span className="text-sm text-stone-500 py-2">Page {page}</span>
          <Button variant="ghost" size="sm" disabled={documents.length < 30} onClick={() => setPage(p => p + 1)}>Suivant</Button>
        </div>
      )}
    </div>
  );
}
